/**
 * PdfViewerModal — modal in-app para visualizar certificados PDF.
 *
 * Motivação (ago/2026 — v2.3.2):
 * O fluxo anterior usava `window.open(blobUrl, "_blank")` que era:
 *  - Bloqueado por popup blocker em desktop (após `await` do fetch,
 *    o browser não considera mais o click como gesto do usuário).
 *  - Silenciosamente ignorado em mobile web (Safari iOS/Chrome Android).
 *  - Frustrante mesmo quando funcionava: forçava troca de aba/app.
 *
 * Este modal resolve tudo em uma UX unificada:
 *  1. Mostra loading state IMEDIATO ao clicar (spinner + mensagem).
 *  2. Fetch do PDF (rastreia progresso se `Content-Length` disponível).
 *  3. Renderiza o PDF INLINE via <iframe> com blob URL.
 *  4. Botões nativos: "Baixar" (anchor direto — não é bloqueado),
 *     "Compartilhar" (Web Share API), "Fechar".
 *  5. Funciona em: desktop, mobile web, PWA (web app).
 *  6. No app nativo (Capacitor) o fluxo continua usando o FileViewer
 *     nativo — este modal é só para o mundo web/PWA.
 */

import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Share2, Loader2, X, ExternalLink, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/clipboard";

/**
 * Monta a mensagem de compartilhamento do certificado.
 * Ex.: "Acabei de concluir o curso Processo Legislativo Municipal do CEAP BRASIL! 🎉
 *       Confira a autenticidade e baixe em PDF: https://appceapbrasil.com/validar/ABC123"
 */
function buildShareMessage(courseName, validationUrl) {
  const course = (courseName || "").trim();
  const parts = [];
  if (course) {
    parts.push(`Acabei de concluir o curso "${course}" do CEAP BRASIL! 🎉`);
  } else {
    parts.push("Acabei de receber meu certificado do CEAP BRASIL! 🎉");
  }
  if (validationUrl) {
    parts.push(`Confira a autenticidade e baixe em PDF: ${validationUrl}`);
  }
  return parts.join("\n\n");
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} props.title - "Certificado — {nome do curso}"
 * @param {string} [props.courseName] - nome do curso (usado na mensagem de compartilhar)
 * @param {string} props.filename - nome do arquivo p/ download
 * @param {() => Promise<Response>} props.fetchFn - função que retorna a Response do PDF
 * @param {string} [props.validationUrl] - link de validação para copiar/compartilhar
 * @param {() => void} [props.onSuccess] - callback quando PDF carrega
 */
export function PdfViewerModal({
  open,
  onClose,
  title,
  courseName,
  filename,
  fetchFn,
  validationUrl,
  onSuccess,
}) {
  const [state, setState] = useState("idle"); // idle | loading | ready | error
  const [progress, setProgress] = useState(0); // 0..100 (só se Content-Length)
  const [blobUrl, setBlobUrl] = useState(null);
  const [blob, setBlob] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const abortRef = useRef(null);
  const startedRef = useRef(false);

  // Ao abrir, dispara o fetch. Ao fechar, revoga o blob URL.
  useEffect(() => {
    if (!open) {
      // Cleanup ao fechar
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch { /* ignore */ }
      }
      if (blobUrl) {
        try { URL.revokeObjectURL(blobUrl); } catch { /* ignore */ }
      }
      setState("idle");
      setProgress(0);
      setBlobUrl(null);
      setBlob(null);
      setErrorMsg("");
      startedRef.current = false;
      return;
    }
    // Impede refetch se re-render acontecer com open=true
    if (startedRef.current) return;
    startedRef.current = true;

    const controller = new AbortController();
    abortRef.current = controller;

    (async () => {
      setState("loading");
      setProgress(0);
      try {
        const res = await fetchFn(controller.signal);
        if (!res || !res.ok) {
          if (res && res.status === 403) {
            // Deixa o chamador tratar (survey gate)
            setState("error");
            setErrorMsg("survey_required");
            return;
          }
          throw new Error(`HTTP ${res?.status || "?"}`);
        }

        // Se o backend enviou Content-Length, rastreamos o progresso.
        // Caso contrário, o spinner permanece "indeterminado".
        const totalStr = res.headers.get("Content-Length");
        const total = totalStr ? parseInt(totalStr, 10) : 0;
        let chunks = [];
        let received = 0;

        const reader = res.body?.getReader?.();
        if (reader) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            chunks.push(value);
            received += value.byteLength;
            if (total > 0) setProgress(Math.min(99, Math.round((received / total) * 100)));
          }
        } else {
          // Browser antigo — cai para res.blob() direto
          const b = await res.blob();
          setBlob(b);
          setBlobUrl(URL.createObjectURL(b));
          setProgress(100);
          setState("ready");
          onSuccess?.();
          return;
        }

        // Monta o blob final
        const b = new Blob(chunks, { type: "application/pdf" });
        if (!b || b.size < 1024) {
          throw new Error("PDF vazio");
        }
        setBlob(b);
        setBlobUrl(URL.createObjectURL(b));
        setProgress(100);
        setState("ready");
        onSuccess?.();
      } catch (err) {
        if (err?.name === "AbortError") return;
        // eslint-disable-next-line no-console
        console.error("[PdfViewerModal] fetch error:", err);
        setErrorMsg(err?.message || "Erro desconhecido");
        setState("error");
      }
    })();

    return () => {
      try { controller.abort(); } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Baixa o PDF — anchor click síncrono direto do gesto do usuário =
  // NÃO é bloqueado por popup blocker.
  const handleDownload = () => {
    if (!blob || !blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || "certificado.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("Certificado baixado");
  };

  // Compartilha via Web Share API v2 (com arquivo) — funciona no Chrome
  // Android, iOS Safari 15+ e Edge. Fallback: copia mensagem completa.
  const handleShare = async () => {
    if (!blob) return;
    const shareText = buildShareMessage(courseName, validationUrl);
    try {
      if (navigator.canShare) {
        const file = new File([blob], filename, { type: "application/pdf" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: courseName ? `Certificado — ${courseName}` : "Meu Certificado CEAP",
            text: shareText,
            files: [file],
          });
          toast.success("Certificado compartilhado");
          return;
        }
      }
      // Fallback: Web Share API v1 (só texto + URL, sem arquivo)
      if (typeof navigator.share === "function") {
        await navigator.share({
          title: courseName ? `Certificado — ${courseName}` : "Certificado CEAP",
          text: shareText,
          url: validationUrl || undefined,
        });
        return;
      }
      // Último recurso: copia a mensagem inteira
      const ok = await copyToClipboard(shareText);
      if (ok) toast.success("Mensagem copiada — cole no WhatsApp/E-mail");
      else toast.error("Não foi possível compartilhar");
    } catch (err) {
      if (err?.name === "AbortError") return;
      toast.error("Falha ao compartilhar");
    }
  };

  /**
   * Abre o WhatsApp direto com a mensagem pré-preenchida.
   * - Em mobile: usa `whatsapp://` que abre o app se instalado.
   * - Em desktop: usa `https://wa.me/` que abre WhatsApp Web.
   * - Se Web Share API com arquivos está disponível E o usuário tocar
   *   neste botão no mobile, ainda assim vamos pelo esquema whatsapp:// —
   *   é mais direto (não passa pelo "escolha app").
   *
   * Obs.: o WhatsApp NÃO aceita anexar arquivo via URL — só texto/link.
   * Por isso a UX é: aluno primeiro vê o certificado, depois clica em
   * "Baixar PDF" se quer o arquivo, ou "WhatsApp" para mandar a mensagem
   * com o link de validação. O link abre a página pública onde qualquer
   * um pode ver e baixar o PDF.
   */
  const handleWhatsApp = () => {
    const shareText = buildShareMessage(courseName, validationUrl);
    const encoded = encodeURIComponent(shareText);
    // Mobile: whatsapp:// abre o app direto. Desktop: wa.me abre WhatsApp Web.
    // Testamos o schema mobile primeiro; se falhar, cai no wa.me.
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const url = isMobile
      ? `whatsapp://send?text=${encoded}`
      : `https://wa.me/?text=${encoded}`;
    // Abre em nova aba (click síncrono do gesto do usuário = não bloqueia)
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // "Abrir em nova aba" — click direto = não é bloqueado
  const handleOpenNewTab = () => {
    if (!blobUrl) return;
    window.open(blobUrl, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent
        className="max-w-3xl w-[95vw] h-[92vh] p-0 gap-0 rounded-2xl overflow-hidden flex flex-col"
        data-testid="pdf-viewer-modal"
      >
        {/* HEADER com título + botões de ação sempre visíveis no topo.
            Motivação (ago/2026): no iOS Safari o PDF ocupa o viewport todo
            e "empurra" a barra de ações inferior para fora da tela. Colocar
            os botões no topo garante que fiquem sempre visíveis sem scroll. */}
        <DialogHeader className="px-3 py-2 border-b border-border bg-card shrink-0">
          <div className="flex items-center gap-2">
            <DialogTitle className="text-sm sm:text-base truncate flex-1 min-w-0" data-testid="pdf-viewer-title">
              {title || "Certificado"}
            </DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="shrink-0 h-8 w-8"
              data-testid="pdf-viewer-close"
              aria-label="Fechar"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          {/* Barra de ações — visível assim que o PDF carrega */}
          {state === "ready" && (
            <div className="flex flex-wrap gap-2 pt-2" data-testid="pdf-viewer-actions">
              <Button
                size="sm"
                onClick={handleDownload}
                data-testid="pdf-viewer-download"
                className="flex-1 sm:flex-initial min-w-0"
              >
                <Download className="w-4 h-4 mr-1" />
                Baixar PDF
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleWhatsApp}
                data-testid="pdf-viewer-whatsapp"
                className="flex-1 sm:flex-initial min-w-0 border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10"
                title="Enviar mensagem pelo WhatsApp"
              >
                <MessageCircle className="w-4 h-4 mr-1" />
                WhatsApp
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleShare}
                data-testid="pdf-viewer-share"
                className="flex-1 sm:flex-initial min-w-0"
                title="Compartilhar via outras opções"
              >
                <Share2 className="w-4 h-4 mr-1" />
                <span className="hidden xs:inline">Compartilhar</span>
                <span className="xs:hidden">Mais</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenNewTab}
                data-testid="pdf-viewer-new-tab"
                title="Abrir em nova aba"
                className="hidden sm:inline-flex"
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Nova aba
              </Button>
            </div>
          )}
        </DialogHeader>

        {/* Área do PDF — fica 100% da altura restante */}
        <div className="flex-1 bg-muted/30 relative overflow-hidden min-h-0">
          {state === "loading" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 gap-4 text-center" data-testid="pdf-viewer-loading">
              <Loader2 className="w-12 h-12 text-[hsl(var(--primary))] animate-spin" />
              <div className="space-y-1">
                <p className="font-medium">Emitindo seu certificado…</p>
                <p className="text-sm text-muted-foreground">
                  Isso pode levar alguns segundos. Não feche esta janela.
                </p>
              </div>
              {progress > 0 && (
                <div className="w-64 h-2 bg-muted rounded-full overflow-hidden" data-testid="pdf-viewer-progress">
                  <div
                    className="h-full bg-[hsl(var(--primary))] transition-all duration-200"
                    style={{ width: `${progress}%` }}
                  />
                  <p className="text-xs text-muted-foreground mt-1">{progress}%</p>
                </div>
              )}
            </div>
          )}

          {state === "error" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 gap-3 text-center" data-testid="pdf-viewer-error">
              <div className="w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center">
                <X className="w-7 h-7 text-destructive" />
              </div>
              <p className="font-medium">
                {errorMsg === "survey_required"
                  ? "Responda a pesquisa antes de emitir"
                  : "Não foi possível emitir o certificado"}
              </p>
              <p className="text-sm text-muted-foreground max-w-md">
                {errorMsg === "survey_required"
                  ? "Complete a pesquisa de satisfação do curso para liberar o download."
                  : "Verifique sua conexão e tente novamente. Se persistir, contate o suporte."}
              </p>
              <Button variant="outline" onClick={onClose} className="mt-2">
                Fechar
              </Button>
            </div>
          )}

          {state === "ready" && blobUrl && (
            // <object> é o elemento HTML mais adequado para PDFs embutidos —
            // renderiza usando o plugin de PDF nativo do browser (PDFium no
            // Chrome/Edge, PDFKit no Safari, PDF.js no Firefox).
            // O <iframe> interno é fallback caso o <object> não consiga
            // renderizar (browsers muito antigos ou in-app browsers).
            <object
              data={blobUrl}
              type="application/pdf"
              className="w-full h-full bg-white"
              data-testid="pdf-viewer-iframe"
              aria-label="Visualizador de certificado PDF"
            >
              <iframe
                src={blobUrl}
                title="Certificado PDF"
                className="w-full h-full border-0 bg-white"
              >
                <div className="p-6 text-center space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Seu navegador não consegue exibir o PDF aqui. Use os
                    botões acima para baixar ou abrir em outra aba.
                  </p>
                  <a
                    href={blobUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary underline text-sm"
                  >
                    Abrir PDF em nova aba
                  </a>
                </div>
              </iframe>
            </object>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
